import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import {
  TENANT_ADMIN_ASSIGNABLE_ROLES,
  TENANT_ROLES,
  canPerform,
  type TenantRole,
} from "@vital/shared";
import { useAuth, useActiveOrganization } from "../context/AuthContext";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { InspectPanel } from "../components/InspectPanel";
import { OpsEmptyState, SectionHeader, InventorySkeleton } from "../components/ops-display";
import {
  MembershipInspectSummary,
  MembershipsFilterBar,
  MembershipsInventoryList,
} from "../components/memberships-inventory";
import { api, ApiError } from "../lib/api";
import {
  MEMBERSHIP_ROLE_LABELS,
  MEMBERSHIPS_PAGE_LABEL,
  MEMBERSHIPS_PAGE_NOTE,
  filterAndSortMemberships,
  type MembershipListRow,
  type MembershipSortKey,
} from "../lib/memberships-ops";

export function MembershipsPage() {
  const { user } = useAuth();
  const activeOrg = useActiveOrganization();
  const organizationLabel =
    activeOrg.code || activeOrg.displayName || activeOrg.organizationId || "—";

  const canManage = canPerform({
    platformRole: user?.platformRole ?? null,
    membershipRole: user?.membershipRole ?? null,
    legacyRole: user?.role,
    permission: "org.membership.manage",
  });
  const isPlatformOwner = user?.platformRole === "owner";
  const assignableRoles = useMemo(
    () =>
      (isPlatformOwner ? TENANT_ROLES : TENANT_ADMIN_ASSIGNABLE_ROLES) as readonly TenantRole[],
    [isPlatformOwner],
  );

  const [items, setItems] = useState<MembershipListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [query, setQuery] = useState("");
  const [listFilter, setListFilter] = useState("all");
  const [sort, setSort] = useState<MembershipSortKey>("name");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<TenantRole>("editor");
  const [password, setPassword] = useState("");
  const [actorPassword, setActorPassword] = useState("");

  const [actionTarget, setActionTarget] = useState<{
    row: MembershipListRow;
    kind: "activate" | "suspend" | "remove" | "role";
  } | null>(null);
  const [actionPassword, setActionPassword] = useState("");
  const [actionRole, setActionRole] = useState<TenantRole>("editor");
  const [acting, setActing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listMemberships();
      setItems(res.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری ناموفق");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedId && !items.some((r) => r.id === selectedId)) {
      setSelectedId(null);
    }
  }, [items, selectedId]);

  const filtered = useMemo(
    () => filterAndSortMemberships(items, { query, filter: listFilter, sort }),
    [items, query, listFilter, sort],
  );

  const selected = useMemo(
    () => items.find((r) => r.id === selectedId) ?? null,
    [items, selectedId],
  );

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.inviteMembership({
        email: email.trim(),
        displayName: displayName.trim(),
        role,
        password: password || undefined,
        actorPassword,
      });
      setSuccess("دعوت عضویت ثبت شد.");
      setEmail("");
      setDisplayName("");
      setPassword("");
      setActorPassword("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "دعوت ناموفق");
    } finally {
      setSaving(false);
    }
  }

  async function handleAction(e: FormEvent) {
    e.preventDefault();
    if (!actionTarget) return;
    setActing(true);
    setError(null);
    setSuccess(null);
    try {
      const { row, kind } = actionTarget;
      if (kind === "activate") {
        await api.activateMembership(row.id, actionPassword);
        setSuccess(`عضویت «${row.displayName}» فعال شد.`);
      } else if (kind === "suspend") {
        await api.suspendMembership(row.id, actionPassword);
        setSuccess(`عضویت «${row.displayName}» معلق شد و نشست‌ها باطل شدند.`);
      } else if (kind === "remove") {
        await api.removeMembership(row.id, actionPassword);
        setSuccess(`عضویت «${row.displayName}» حذف شد و نشست‌ها باطل شدند.`);
      } else {
        await api.updateMembershipRole(row.id, actionRole, actionPassword);
        setSuccess(`نقش «${row.displayName}» به‌روز شد.`);
      }
      setActionTarget(null);
      setActionPassword("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "عملیات ناموفق");
    } finally {
      setActing(false);
    }
  }

  function renderMembershipActions(row: MembershipListRow): ReactNode {
    return (
      <>
        {(row.status === "invited" || row.status === "suspended") && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={!activeOrg.writable}
            onClick={() => {
              setActionTarget({ row, kind: "activate" });
              setActionPassword("");
            }}
          >
            فعال‌سازی
          </button>
        )}
        {row.status === "active" && (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={!activeOrg.writable}
              onClick={() => {
                setActionTarget({ row, kind: "role" });
                setActionRole(
                  assignableRoles.includes(row.role as TenantRole)
                    ? (row.role as TenantRole)
                    : "editor",
                );
                setActionPassword("");
              }}
            >
              تغییر نقش
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={!activeOrg.writable || row.userId === user?.id}
              onClick={() => {
                setActionTarget({ row, kind: "suspend" });
                setActionPassword("");
              }}
            >
              تعلیق
            </button>
          </>
        )}
        {row.status !== "removed" && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={!activeOrg.writable || row.userId === user?.id}
            onClick={() => {
              setActionTarget({ row, kind: "remove" });
              setActionPassword("");
            }}
          >
            حذف عضویت
          </button>
        )}
      </>
    );
  }

  if (!canManage) {
    return (
      <>
        <PageHeader title={MEMBERSHIPS_PAGE_LABEL} />
        <p className="banner banner--error" role="alert">
          دسترسی مدیریت عضویت ندارید.
        </p>
      </>
    );
  }

  return (
    <>
      <PageHeader title={MEMBERSHIPS_PAGE_LABEL} />

      <aside
        className="ops-safety-summary ops-safety-summary--privacy"
        role="note"
        data-vt-memberships-safety
      >
        <p className="ops-safety-summary__eyebrow">حریم عضویت</p>
        <ul className="ops-safety-summary__points">
          <li>وضعیت عضویت با وضعیت حساب ورود فرق دارد</li>
          <li>بدون رمز، توکن دعوت، راز MFA، یا دادهٔ نشست</li>
          <li>دامنه: سازمان فعال — سازمان فقط‌خواندنی دعوت و تغییر را مسدود می‌کند</li>
        </ul>
        <details className="ops-safety-disclosure">
          <summary>متن کامل حریم عضویت</summary>
          <p>{MEMBERSHIPS_PAGE_NOTE}</p>
        </details>
      </aside>

      {error && (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="banner banner--success" role="status">
          {success}
        </p>
      )}

      {!activeOrg.writable && (
        <div className="tenant-banner tenant-banner--warning" role="status">
          سازمان فعال فقط‌خواندنی است؛ دعوت و تغییر عضویت مسدود است.
        </div>
      )}

      <div className="page-stack page-stack--inventory">
        <div
          className={`content-ops-layout${selected ? " content-ops-layout--inspect-open" : ""}`}
          data-vt-content-ops
          data-vt-inspect-open={selected ? "true" : "false"}
        >
          <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
            <SectionHeader title="فهرست عضویت‌ها" />
            <MembershipsFilterBar
              filter={listFilter}
              onFilterChange={setListFilter}
              query={query}
              onQueryChange={setQuery}
              sort={sort}
              onSortChange={setSort}
              organizationLabel={organizationLabel}
            />
            {loading ? (
              <InventorySkeleton />
            ) : items.length === 0 ? (
              <OpsEmptyState title="عضوی ثبت نشده است" />
            ) : filtered.length === 0 ? (
              <OpsEmptyState title="موردی در این فیلتر نیست" />
            ) : (
              <MembershipsInventoryList
                rows={filtered}
                selectedId={selectedId}
                currentUserId={user?.id}
                onSelect={setSelectedId}
                renderActions={renderMembershipActions}
              />
            )}
          </Card>
          {selected && (
            <InspectPanel title="جزئیات عضویت" className="content-inspect-panel">
              <MembershipInspectSummary
                item={selected}
                organizationLabel={organizationLabel}
                isSelf={selected.userId === user?.id}
                actions={renderMembershipActions(selected)}
              />
            </InspectPanel>
          )}
        </div>

        <Card title="دعوت عضو جدید">
          <form className="content-form content-form--in-card" onSubmit={handleInvite}>
            <label>
              <span>نام نمایشی</span>
              <input
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                disabled={!activeOrg.writable || saving}
              />
            </label>
            <label>
              <span>ایمیل</span>
              <input
                type="email"
                dir="ltr"
                className="ltr-isolate"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="off"
                disabled={!activeOrg.writable || saving}
              />
            </label>
            <label>
              <span>نقش سازمانی</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as TenantRole)}
                disabled={!activeOrg.writable || saving}
              >
                {assignableRoles.map((r) => (
                  <option key={r} value={r}>
                    {MEMBERSHIP_ROLE_LABELS[r] ?? r}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>رمز اولیه (فقط برای کاربر جدید)</span>
              <input
                type="password"
                dir="ltr"
                className="ltr-isolate"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={12}
                autoComplete="new-password"
                disabled={!activeOrg.writable || saving}
              />
            </label>
            <label>
              <span>تأیید هویت شما (رمز فعلی)</span>
              <input
                type="password"
                dir="ltr"
                className="ltr-isolate"
                value={actorPassword}
                onChange={(e) => setActorPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={!activeOrg.writable || saving}
              />
            </label>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!activeOrg.writable || saving}
            >
              {saving ? "در حال ثبت…" : "دعوت عضو"}
            </button>
          </form>
        </Card>
      </div>

      {actionTarget && (
        <div className="modal-backdrop" role="presentation">
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="membership-action-title"
          >
            <h2 id="membership-action-title">
              {actionTarget.kind === "activate" && "فعال‌سازی عضویت"}
              {actionTarget.kind === "suspend" && "تعلیق عضویت"}
              {actionTarget.kind === "remove" && "حذف عضویت"}
              {actionTarget.kind === "role" && "تغییر نقش عضویت"}
            </h2>
            <p>
              عضو: {actionTarget.row.displayName} (
              <bdi className="ltr-isolate" dir="ltr">
                {actionTarget.row.email}
              </bdi>
              )
            </p>
            <form className="auth-form" onSubmit={handleAction}>
              {actionTarget.kind === "role" && (
                <label>
                  <span>نقش جدید</span>
                  <select
                    value={actionRole}
                    onChange={(e) => setActionRole(e.target.value as TenantRole)}
                    disabled={acting}
                  >
                    {assignableRoles.map((r) => (
                      <option key={r} value={r}>
                        {MEMBERSHIP_ROLE_LABELS[r] ?? r}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                <span>تأیید هویت شما (رمز فعلی)</span>
                <input
                  type="password"
                  dir="ltr"
                  className="ltr-isolate"
                  value={actionPassword}
                  onChange={(e) => setActionPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  disabled={acting}
                />
              </label>
              <div className="page-header__actions">
                <button type="submit" className="btn btn--primary" disabled={acting}>
                  {acting ? "در حال اجرا…" : "تأیید"}
                </button>
                <button
                  type="button"
                  className="btn btn--secondary"
                  disabled={acting}
                  onClick={() => {
                    setActionTarget(null);
                    setActionPassword("");
                  }}
                >
                  انصراف
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
